import { useParams, Link, useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { articles, categories } from '@/mocks/articles';
import { siteConfig } from '@/mocks/site-config';

function formatViews(views: number): string {
  if (views >= 10000) return (views / 10000).toFixed(1) + '万';
  return views.toString();
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);

  const currentCategory = useMemo(() => {
    return categories.find((c) => c.slug === slug) || categories[0];
  }, [slug]);

  const filteredArticles = useMemo(() => {
    if (!slug || slug === 'latest') return articles;
    return articles.filter((a) => a.category === slug);
  }, [slug]);

  const featuredCategory = categories.find((c) => c.slug === slug);

  const perPage = 9;
  const totalPages = Math.ceil(filteredArticles.length / perPage);
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const categoryHeroImages: Record<string, string> = {
    'morisxiu': 'https://readdy.ai/api/search-image?query=Dark%20gossip%20news%20studio%20stage%20with%20dramatic%20red%20spotlight%2C%20breaking%20news%20atmosphere%2C%20microphone%20and%20camera%20equipment%2C%20cinematic%20lighting%20with%20deep%20shadows%2C%20film%20grain%20texture%2C%20mysterious%20and%20intense%20mood&width=1920&height=600&seq=cat-hero-morisxiu&orientation=landscape',
    'british-museum': 'https://readdy.ai/api/search-image?query=Grand%20museum%20interior%20with%20dramatic%20golden%20lighting%2C%20ancient%20artifacts%20and%20classical%20columns%2C%20mysterious%20academic%20atmosphere%2C%20cinematic%20composition%20with%20deep%20contrast%2C%20warm%20tones%20and%20film%20grain&width=1920&height=600&seq=cat-hero-museum&orientation=landscape',
    'star-scandal': 'https://readdy.ai/api/search-image?query=Dramatic%20red%20carpet%20event%20with%20paparazzi%20flashes%2C%20celebrity%20silhouette%20in%20spotlight%2C%20chaotic%20media%20scene%2C%20cinematic%20lighting%20with%20high%20contrast%2C%20black%20and%20red%20color%20scheme%2C%20film%20grain%20texture&width=1920&height=600&seq=cat-hero-scandal&orientation=landscape',
    'wanghong': 'https://readdy.ai/api/search-image?query=Smartphone%20live%20streaming%20setup%20with%20ring%20light%20and%20camera%20equipment%2C%20dark%20room%20with%20dramatic%20screen%20glow%2C%20content%20creator%20studio%20atmosphere%2C%20cinematic%20lighting%20with%20warm%20and%20cool%20contrast&width=1920&height=600&seq=cat-hero-wanghong&orientation=landscape',
    'latest': 'https://readdy.ai/api/search-image?query=Breaking%20news%20headquarters%20with%20multiple%20monitors%20and%20dramatic%20red%20alert%20lighting%2C%20newsroom%20control%20room%20atmosphere%2C%20cinematic%20broadcast%20setting%2C%20dark%20and%20intense%20mood%2C%20film%20grain%20texture&width=1920&height=600&seq=cat-hero-latest&orientation=landscape',
    'deep-dive': 'https://readdy.ai/api/search-image?query=Investigative%20journalism%20desk%20with%20documents%20and%20photos%20pinned%20on%20wall%2C%20dramatic%20desk%20lamp%20lighting%2C%20mysterious%20atmosphere%2C%20detective%20style%20aesthetic%2C%20dark%20and%20moody%20cinematic%20composition&width=1920&height=600&seq=cat-hero-deep&orientation=landscape',
  };

  return (
    <>
      <Header />

      {/* SEO Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `${featuredCategory?.name || '最新爆料'} - ${siteConfig.name}`,
          "description": featuredCategory?.description || siteConfig.description,
          "url": `${siteConfig.url}/category/${slug}`,
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": siteConfig.url },
              { "@type": "ListItem", "position": 2, "name": featuredCategory?.name || '最新爆料', "item": `${siteConfig.url}/category/${slug}` },
            ],
          },
        })}
      </script>

      <main className="pt-16">
        {/* Category Hero */}
        <section className="relative w-full h-[320px] md:h-[400px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={categoryHeroImages[slug || 'latest'] || categoryHeroImages['latest']}
              alt={featuredCategory?.name || '最新爆料'}
              title={`${featuredCategory?.name || '最新爆料'} - 51吃瓜官网`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/30"></div>
          <div className="relative z-10 flex flex-col items-center justify-center h-full px-4 text-center">
            <div className="mb-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-500 text-background-50 text-xs font-label font-semibold rounded-full">
                <i className="ri-fire-line"></i>
                {featuredCategory?.name || '最新爆料'}
              </span>
            </div>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 mb-3">
              {featuredCategory?.name || '最新爆料'}
            </h1>
            <p className="text-sm md:text-base text-background-50/70 max-w-xl">
              {featuredCategory?.description || '全网最新吃瓜动态，第一时间追踪'}
            </p>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-4 bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <nav aria-label="面包屑导航">
              <ol className="flex items-center gap-2 text-xs md:text-sm text-foreground-500">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                </li>
                <span className="text-foreground-300">/</span>
                <li className="text-foreground-700 font-medium">
                  {featuredCategory?.name || '最新爆料'}
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Category Tabs */}
        <section className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setCurrentPage(1);
                    navigate(`/category/${cat.slug}`);
                  }}
                  className={`px-4 py-2 text-sm font-label rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    (slug || 'latest') === cat.slug
                      ? 'bg-foreground-900 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8 md:mb-10">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight">
                {featuredCategory?.name || '全部爆料'}
              </h2>
              <p className="text-sm md:text-base text-foreground-500 mt-2 font-label">
                共 {filteredArticles.length} 篇精彩内容
              </p>
            </div>

            {paginatedArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {paginatedArticles.map((article) => (
                  <article key={article.id} className="group">
                    <Link
                      to={`/article/${article.id}`}
                      className="block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden transition-all duration-300 cursor-pointer hover:border-background-300/80"
                    >
                      <div className="relative overflow-hidden" style={{ aspectRatio: '4/3' }}>
                        <img
                          src={article.image}
                          alt={article.title}
                          title={article.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                        />
                        {article.tag && (
                          <span className={`absolute top-3 left-3 inline-block px-2.5 py-0.5 text-xs font-label font-semibold text-background-50 ${article.tagColor} rounded-full`}>
                            {article.tag}
                          </span>
                        )}
                      </div>

                      <div className="p-4 md:p-5">
                        <div className="flex items-center gap-3 mb-2.5">
                          <span className="text-xs font-label text-primary-500 font-medium">
                            {article.categoryName}
                          </span>
                          <span className="text-xs text-foreground-400">
                            {article.date}
                          </span>
                        </div>

                        <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 leading-snug mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                          {article.title}
                        </h3>

                        <p className="text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>

                        <div className="flex items-center gap-4 text-xs text-foreground-400">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {formatViews(article.views)}阅读
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-chat-3-line"></i>
                            {formatViews(article.comments)}评论
                          </span>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <i className="ri-inbox-line text-4xl text-foreground-300 mb-4"></i>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-10 md:mt-12">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-200 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  aria-label="上一页"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-label cursor-pointer transition-colors ${
                      page === currentPage
                        ? 'bg-foreground-900 text-background-50'
                        : 'border border-background-200 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-200 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  aria-label="下一页"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}